<script>
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import metaAdsAPI from 'dashboard/api/metaAds';

export default {
  components: {
    SettingsTable,
    NextButton,
  },
  data() {
    return {
      leadEvents: [],
      leadEventsCount: 0,
      loadingEvents: false,
    };
  },
  computed: {
    ...mapGetters({
      records: 'metaAds/getConnections',
      uiFlags: 'metaAds/getUIFlags',
    }),
    tableHeaders() {
      return [
        this.$t('META_ADS.LEAD_ADS.TABLE_HEADER.PAGE_NAME'),
        this.$t('META_ADS.LEAD_ADS.TABLE_HEADER.STATUS'),
      ];
    },
    eventHeaders() {
      return [
        this.$t('META_ADS.LEAD_ADS.EVENTS.TABLE.RECEIVED_AT'),
        this.$t('META_ADS.LEAD_ADS.EVENTS.TABLE.LEADGEN_ID'),
        this.$t('META_ADS.LEAD_ADS.EVENTS.TABLE.FORM_ID'),
        this.$t('META_ADS.LEAD_ADS.EVENTS.TABLE.STATUS'),
      ];
    },
  },
  mounted() {
    this.$store.dispatch('metaAds/get');
    this.fetchLeadEvents();
  },
  methods: {
    goToConnections() {
      this.$router.push({
        query: { ...this.$route.query, tab: 'connections' },
      });
    },
    statusLabel(status) {
      const labels = {
        active: this.$t('META_ADS.LEAD_ADS.STATUS.ACTIVE'),
        disconnected: this.$t('META_ADS.LEAD_ADS.STATUS.DISCONNECTED'),
        error: this.$t('META_ADS.LEAD_ADS.STATUS.ERROR'),
      };
      return labels[status] || labels.active;
    },
    async fetchLeadEvents() {
      this.loadingEvents = true;
      try {
        const { data } = await metaAdsAPI.leadEvents();
        this.leadEvents = data.events;
        this.leadEventsCount = data.count;
      } catch (error) {
        this.leadEvents = [];
      } finally {
        this.loadingEvents = false;
      }
    },
    eventStatusLabel(status) {
      const labels = {
        received: this.$t('META_ADS.LEAD_ADS.EVENTS.STATUS.RECEIVED'),
        processed: this.$t('META_ADS.LEAD_ADS.EVENTS.STATUS.PROCESSED'),
        failed: this.$t('META_ADS.LEAD_ADS.EVENTS.STATUS.FAILED'),
      };
      return labels[status] || status;
    },
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('pt-BR');
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.LEAD_ADS.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.LEAD_ADS.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="!uiFlags.fetchingList && !records.length"
      class="flex flex-col items-start gap-3 p-4 text-sm rounded-lg text-n-slate-11 bg-n-alpha-black2"
    >
      {{ $t('META_ADS.LEAD_ADS.LIST.EMPTY') }}
      <NextButton
        icon="i-lucide-plug"
        :label="$t('META_ADS.CONNECTIONS.GO')"
        sm
        @click="goToConnections"
      />
    </div>

    <SettingsTable
      v-else
      :headers="tableHeaders"
      :is-loading="uiFlags.fetchingList"
      :is-empty="!records.length"
      :empty-message="$t('META_ADS.LEAD_ADS.LIST.EMPTY')"
    >
      <tr v-for="connection in records" :key="connection.id">
        <td class="px-5 py-4">
          <div class="flex gap-2 font-medium break-words text-n-slate-12">
            {{ connection.name }}
          </div>
          <div class="block mt-1 text-sm text-n-slate-11">
            {{
              $t('META_ADS.LEAD_ADS.LIST.PAGE_ID', { id: connection.page_id })
            }}
          </div>
        </td>
        <td class="px-5 py-4">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                connection.status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              "
            >
              {{ statusLabel(connection.status) }}
            </span>
            <span
              v-if="connection.leadgen_subscribed"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {{ $t('META_ADS.LEAD_ADS.STATUS.LEADGEN_ON') }}
            </span>
          </div>
        </td>
      </tr>
    </SettingsTable>

    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('META_ADS.LEAD_ADS.EVENTS.HEADER') }}
        </h2>
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-11"
        >
          {{ leadEventsCount }}
        </span>
      </div>
      <p class="text-sm text-n-slate-10">
        {{ $t('META_ADS.LEAD_ADS.EVENTS.DESCRIPTION') }}
      </p>
      <SettingsTable
        :headers="eventHeaders"
        :is-loading="loadingEvents"
        :is-empty="!leadEvents.length"
        :empty-message="$t('META_ADS.LEAD_ADS.EVENTS.EMPTY')"
      >
        <tr v-for="event in leadEvents" :key="event.id">
          <td class="px-5 py-4 text-sm text-n-slate-11">
            {{ formatDate(event.received_at) }}
          </td>
          <td class="px-5 py-4 font-mono text-sm text-n-slate-12">
            {{ event.leadgen_id }}
          </td>
          <td class="px-5 py-4 font-mono text-sm text-n-slate-11">
            {{ event.form_id }}
          </td>
          <td class="px-5 py-4">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                event.status === 'processed'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : event.status === 'failed'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              "
            >
              {{ eventStatusLabel(event.status) }}
            </span>
          </td>
        </tr>
      </SettingsTable>
    </div>
  </div>
</template>
